import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import { Alert, Button, Spinner, Table } from "react-bootstrap";
import { utils, writeFileXLSX } from "xlsx";

import "./Kursliste.css";
import CONFIG from "../config";
import { fetchKursliste } from "../reducers/kursliste-reducer";
import { Navbar, Pagination, SelectDatenSet, Tooltip, BeendenButton } from "../utils/components";
import { formatCurrency, formatDateTime, formatIsoDate, formatNumber, download } from "../utils/functions";
import { searchListe } from "./searchList";
import { SortByAlphaIcon, SortRoundedIcon, Fold, Unfold } from "../utils/components/icons";
import KindMake from "../panel/kinds/kind-make";
import { CollapseForm } from "../utils/components";
import { metalsActions } from "../actions";

const Kursliste = (props) => {
  const { configStorage, kurslisteStorage, panelStorage } = useStore().getState();
  const dispatch = useDispatch();
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(10);
  const [search, setSearch] = useState("");
  const [aktiveKursliste, setAktiveKursliste] = useState(undefined);
  const [labels, setLabels] = useState({});
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ kind: "", make: "" });
  const [sort, setSort] = useState({
    kennziffer: { active: false, directionUp: false },
    bezeichnung: { active: true, directionUp: false },
    betragAK: { active: false, directionUp: false },
    betragVK: { active: false, directionUp: false },
    betragMaKondAK: { active: false, directionUp: false },
    betragMaKondVK: { active: false, directionUp: false },
    betragEndkundeVK: { active: false, directionUp: false },
  });
  const [fold, setFold] = useState([false, false, false, false, false]);

  const header = useMemo(() => ({
    edelmetall: {
      kennziffer: "Kennziffer",
      bezeichnung: "Produkt",
      betragAK: "Ankauf Beratershop",
      betragVK: "Verkauf Beratershop",
      betragMaKondAK: "Ankauf Mitarbeiter Kondition",
      betragMaKondVK: "Verkauf Mitarbeiter Kondition",
      betragEndkundeVK: "Verkauf Kundenshop",
    },
    sorten: {
      kennziffer: "Kennziffer",
      isoCode: "ISO",
      bezeichnung: "Bezeichnung",
      betragAK: "Ankauf Beratershop",
      betragVK: "Verkauf Beratershop",
      betragMaKondAK: "Ankauf Mitarbeiter Kondition",
      betragMaKondVK: "Verkauf Mitarbeiter Kondition",
      betragEndkundeVK: "Verkauf Kundenshop",
    }
  }), []);

  useEffect(() => {
    setLabels(header[aktiveKursliste] || {});
  }, [aktiveKursliste, header]);

  const headerText = useMemo(() => {
    const shopListe = configStorage.getShopListe() || [];
    if (shopListe.length === 1 && shopListe[0] === "edelmetall") return "Preisliste";
    if (shopListe.length === 1 && shopListe[0] === "sorten") return "Kursliste";
    return "Kurs-/Preislisten";
  }, [configStorage]);

  const loadData = useCallback(async (appType) => {
    if (kurslisteStorage.isLoading()) return;
    const shopAppType = CONFIG.getShopConfig({
      edelmetall: "edelmetall",
      sorten: "sorten"
    });
    if (!appType) {
      appType = (props || {}).select;
    }
    if (!appType) {
      appType = shopAppType;
      const shopListe = configStorage.getShopListe() || [];
      if (!shopListe.find(s => s === appType)) appType = shopListe[0];
    }
    if (!appType) return;

    if (aktiveKursliste !== appType) {
      setPage(1);
      setSearch("");
    }

    const apiCall = configStorage.getApiRequest(appType + "Kursliste");
    let res;
    try {
      res = await Promise.resolve(dispatch(fetchKursliste({ apiCall })));
    } catch (e) {
      console.error(e);
      setError(e.message);
      return;
    }
    setLoaded(true);
    if (!res || res.error) {
      setError((res || {}).error || "Daten konten nicht geladen werden");
      return;
    }
    // hole edelmetall katalog für die Namen der kind/make
    if (appType === "edelmetall") {
      const baseRequest = configStorage.getRequestData("basedata");
      baseRequest.restRoot = configStorage.getRestRoot();
      delete baseRequest.url;
      dispatch(metalsActions.fetchBaseData(baseRequest));
    }
    setError(null);
    setAktiveKursliste(appType);
  }, [aktiveKursliste, configStorage, dispatch, kurslisteStorage, props]);

  useEffect(() => {
    // load required data only once
    if (!loaded) {
      loadData();
    }
  }, [loadData, loaded]);

  useEffect(() => {
    const newFilter = { ...filter };
    let update = false;
    if (panelStorage.getMake() !== filter.make) {
      newFilter.make = panelStorage.getMake();
      update = true;
    }
    if (panelStorage.getKind() !== filter.kind) {
      newFilter.kind = panelStorage.getKind();
      update = true;
    }
    if (update && aktiveKursliste !== "sorten") {
      setFilter(newFilter);
    }
    if (aktiveKursliste === "sorten" && Object.keys(filter).length > 0) {
      setFilter({});
    }

  }, [filter, panelStorage, aktiveKursliste]);

  const exportFile = useCallback(() => {
    const format = { edelmetall: "#,##0.00 €", sorten: "0.000" };
    const blattTitel = { edelmetall: "Preisliste_Edelmetalle", sorten: "Kursliste_Sorten" };
    const makes = kurslisteStorage.makes;
    const kinds = kurslisteStorage.kinds;
    const headers = Object.values(header[aktiveKursliste]);
    const sliceCount = aktiveKursliste === "sorten" ? 3 : 2;

    let liste = kurslisteStorage.getListe();

    // Apply filter settings to the export
    const isSorten = aktiveKursliste === "sorten";
    const searchParams = isSorten ? { search: "", filter: null } : { search: "", filter };
    liste = liste.filter(item => searchListe(item, searchParams));
    let data_headers = Object.keys(header[aktiveKursliste]);
    if (
      aktiveKursliste === "edelmetall" &&
      (
        Object.keys(makes).length > 0 ||
        Object.keys(kinds).length > 0
      )
    ) {
      headers.push("Metallart");
      headers.push("Ausführung");
      data_headers.push("make");
      data_headers.push("kind");
      liste = liste.map(item => {
        if (!item.kind && !item.make) return item;
        const kind = item.kind ? kinds[item.kind] || "" : "";
        const make = item.make ? makes[item.make] || "" : "";
        return { ...item, kind, make };
      });
    }
    const ws = utils.json_to_sheet(liste, { header: data_headers });
    const wb = utils.book_new(ws, blattTitel[aktiveKursliste]);
    utils.sheet_add_aoa(ws, [headers], { origin: "A1" });
    const max_width = liste.reduce((prev, item) => Math.max(prev, String(item.bezeichnung).length), 10);
    const valCols = headers.slice(sliceCount);
    if (aktiveKursliste === "sorten") {
      ws["!cols"] = [{ wch: 10 }, { wch: 4 }, { wch: max_width }, ...valCols.map(() => ({ wch: 18 }))];
    } else {
      ws["!cols"] = [{ wch: 10 }, { wch: max_width }, ...valCols.map(() => ({ wch: 18 }))];
    }
    liste.forEach((_item, idx) => {
      let addr;
      valCols.forEach((_value, col) => {
        addr = utils.encode_cell({ r: idx + 1, c: col + sliceCount });
        if (ws[addr]) ws[addr].z = format[aktiveKursliste];
      });
    });
    utils.sheet_add_aoa(ws, [["Stand", formatDateTime(kurslisteStorage.timestamp)]], { origin: utils.encode_cell({ r: liste.length + 2, c: 0 }) });
    writeFileXLSX(wb, blattTitel[aktiveKursliste] + "_" + formatIsoDate(kurslisteStorage.timestamp).replace(/:/g, "-") + ".xlsx");
  }, [aktiveKursliste, header, kurslisteStorage, filter]);

  const exportPdf = useCallback(() => {
    const blattTitel = { edelmetall: "Preisliste_Edelmetall", sorten: "Kursliste_Sorten" };
    const pdfName = blattTitel[aktiveKursliste] + "_" + formatIsoDate(kurslisteStorage.timestamp).replace(/:/g, "-") + ".pdf";
    let apiCall = configStorage.getApiRequest(aktiveKursliste + "KurslistePdf");

    // Build filter options to send to backend
    const opts = {};
    if (filter.kind) opts.kind = filter.kind;
    if (filter.make) opts.make = filter.make;

    apiCall(opts, (error, data, response) => {
      if (error) {
        setError(error.message);
        return;
      }
      if (response.body) download(response.body, pdfName);
    });
  }, [aktiveKursliste, configStorage, kurslisteStorage.timestamp, filter]);

  const onNavbarChange = useCallback((changes) => {
    if (!changes) return;
    if (typeof changes.search === "string") {
      if (kurslisteStorage.getListe().filter(item => searchListe(item, { search: changes.search, filter })).length < rows * (page - 1)) {
        setPage(1);
      }
      setSearch(changes.search);
    }
    if (changes.page) {
      setPage(Number(changes.page));
    }
    if (changes.rows) {
      // first element in the page
      let pos = (page - 1) * rows + 1;
      // new page to display first element
      let newRows = Number(changes.rows) || 10;
      setPage(Math.ceil(pos / newRows));
      setRows(newRows);
    }
    if (typeof changes.changed === "boolean") {
      if (kurslisteStorage.getListe().filter(item => searchListe(item, { search: changes.search, filter })).length < rows * (page - 1)) {
        setPage(1);
      }
    }
  }, [filter, kurslisteStorage, page, rows]);

  const toggleSort = useCallback((e) => {
    if (!e) return;
    let sortIdx = e.target && e.target.dataset ? e.target.dataset.sort : "";
    if (!sortIdx && e.currentTarget && e.currentTarget.dataset) sortIdx = e.currentTarget.dataset.sort;
    if (!sort || !sort[sortIdx]) return;
    let newSort = { ...sort };
    if (newSort[sortIdx].active) {
      newSort[sortIdx].directionUp = !newSort[sortIdx].directionUp;
    } else {
      Object.keys(newSort).forEach(sKey => { newSort[sKey].active = false; });
      newSort[sortIdx].active = true;
    }
    setSort(newSort);
  }, [sort]);

  const toggleFold = useCallback((e) => {
    const target = e && (e.currentTarget || e.target);
    if (target) {
      const col = target.dataset.col;
      if (isNaN(col) || typeof fold[Number(col)] !== "boolean") return;
      const newFold = [...fold];
      newFold[col] = !newFold[col];
      setFold(newFold);
    }
  }, [fold]);

  const onKeyPress = useCallback((e) => {
    const target = e && (e.currentTarget || e.target);
    if (!e || !target || (e.key !== "Enter" && e.key !== " ")) return;
    // simmulate click on RETURN or SPACE
    e.preventDefault();
    toggleFold(e);
    toggleSort(e);
    return false;
  }, [toggleFold, toggleSort]);

  const onChangeListe = useCallback((e) => {
    if (!e) return;
    const target = e.target || e.currentTarget;
    if (!target) return;
    loadData(target.value);
    setLabels(header[target.value] || {});
  }, [loadData, header]);

  const sortListe = (a, b) => {
    const sortIndex = Object.keys(sort).find(skey => sort[skey].active);
    const sortDir = sort[sortIndex].directionUp ? 1 : -1;
    let s1, s2, b1, b2, n1, n2;
    s1 = String(a[sortIndex]);
    s2 = String(b[sortIndex]);
    b1 = s1.replace(/^([0-9,.]*( EUR)?)(.*)/, "$3").trim().toLowerCase();
    b2 = s2.replace(/^([0-9,.]*( EUR)?)(.*)/, "$3").trim().toLowerCase();
    n1 = Number(s1.replace(/^([0-9,]*)(.*)/, "$1"));
    n2 = Number(s2.replace(/^([0-9,]*)(.*)/, "$1"));
    if (b1 === b2) return n1 < n2 ? sortDir : -sortDir;
    return b1 < b2 ? sortDir : -sortDir;
  };

  if (kurslisteStorage.isLoading()) {
    return (
      <div>
        <h1>{headerText}</h1>
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error || kurslisteStorage.getError()) {
    return <div>
      <h1>{headerText}</h1>
      <div className="alert alert-danger fs-6 p-1">{error || kurslisteStorage.getError()}</div>
      <SelectDatenSet
        label="Auswahl der Liste"
        value={aktiveKursliste}
        onChange={onChangeListe}
        dataSet={configStorage.getShopListe()}
      />
    </div>;
  }
  if (!kurslisteStorage.hasData()) {
    return null;
  }

  const isSorten = aktiveKursliste === "sorten";
  // const labels = header[aktiveKursliste] || {};
  const disabled = false;
  const liste = kurslisteStorage.getListe();
  const searchParams = isSorten ? { search, filter: null } : { search, filter };
  const treffer = liste.filter(item => searchListe(item, searchParams)).sort(sortListe);
  const brand = isSorten ? "Sorten" : "Edelmetalle";
  const start = (page - 1) * rows + 1;
  let end = start - 1 + rows;
  if (end > treffer.length) end = treffer.length;
  const keys = ["betragAK", "betragVK", "betragMaKondAK", "betragMaKondVK", "betragEndkundeVK"];

  const formater = isSorten
    ? (betrag) => {
      return isNaN(betrag) || betrag === null ? "X" : formatNumber(betrag, {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      });
    } : (betrag) => {
      return isNaN(betrag) || betrag === null ? "X" : formatCurrency(betrag);
    };

  return <div>
    <h1>{headerText}</h1>
    <div className="d-flex justify-content-between">
      <SelectDatenSet
        label="Auswahl der Liste"
        value={aktiveKursliste}
        onChange={onChangeListe}
        dataSet={configStorage.getShopListe()}
      />
      <div>
        <span className="me-one">Stand: {formatDateTime(kurslisteStorage.timestamp)}</span>
        <Button onClick={exportFile} disabled={!liste.length}>Export XLSX</Button>
        <Button onClick={exportPdf} className="ms-one" disabled={!liste.length}>Export PDF</Button>
      </div>
    </div>
    {!isSorten && (kurslisteStorage.getMakes().length > 1 || kurslisteStorage.getKinds().length > 1)
      ? <CollapseForm id="SKNobleMetals" className="mt-0" title="Filterparameter">
        <KindMake makes={kurslisteStorage.getMakes()} kinds={kurslisteStorage.getKinds()} />
      </CollapseForm>
      : null
    }
    <Navbar
      brand={brand}
      pagination={Pagination}
      searchListe={searchListe}
      list={liste}
      page={page}
      rows={rows}
      search={searchParams}
      disabled={disabled}
      onChange={onNavbarChange}
    />
    {!liste.length
      ? <Alert variant="warning">Es konnten keine passenden Kurse gefunden werden</Alert>
      : <Table className="condensed" variant="hover" size="sm" responsive aria-description="Liste der Kurse im EdelmetallShop oder SortenShop verfügbar sind.">
        <caption>{brand}: {start}-{end} von {treffer.length}</caption>
        <thead>
          <tr>
            <th key="kennziffer" className={"kennziffer" + (sort["kennziffer"].active ? " sortindex" : "")}>
              <div className="text-end ">
                Kenn-<span className="nowrap">ziffer<SortRoundedIcon
                  data-sort="kennziffer"
                  tabIndex="0"
                  aria-hidden="false"
                  onClick={toggleSort}
                  onKeyPress={onKeyPress}
                  className={"ms-1" + (sort["kennziffer"].directionUp ? "" : " flipped")
                  }
                />
                </span>
              </div>
            </th>
            {labels.isoCode
              ? <th key="produkt" className="iso">
                <div className="text-start nowrap">
                  {labels.isoCode}
                </div>
              </th>
              : null
            }
            <th key="produkt" className={"bezeichnung" + (sort["bezeichnung"].active ? " sortindex" : "")}>
              <div className="text-start nowrap">
                {labels.bezeichnung}
                <SortByAlphaIcon
                  data-sort="bezeichnung"
                  tabIndex="0"
                  aria-hidden="false"
                  onClick={toggleSort}
                  onKeyPress={onKeyPress}
                  className="ms-1"
                />
              </div>
            </th>
            {keys.map((labelKey, idx) => {
              if (!labels[labelKey]) return null;
              return <th key={labelKey}>
                {!fold[idx]
                  ? <div>
                    <span>{labels[labelKey]}</span>
                    <div className="d-flex justify-content-between">
                      <Fold
                        data-col={idx}
                        onClick={toggleFold}
                        onKeyPress={onKeyPress}
                        tabIndex="0"
                        aria-hidden="false"
                      />
                      <SortRoundedIcon
                        data-sort={labelKey}
                        tabIndex="0"
                        aria-hidden="false"
                        onClick={toggleSort}
                        onKeyPress={onKeyPress}
                        className={"ms-1" + ((sort[labelKey] || {}).directionUp ? "" : " flipped")
                        }
                      />
                    </div>
                  </div>
                  : <Tooltip
                    title={labels[labelKey]}
                    icon={Unfold}
                    data-col={idx}
                    onClick={toggleFold}
                    onKeyPress={onKeyPress}
                    tabIndex="0"
                    aria-hidden="false"
                  />
                }
              </th>;
            })
            }
          </tr>
        </thead>
        <tbody>
          {treffer.slice(start - 1, start - 1 + rows).map(item => {
            return <tr
              key={"kurs" + String(item.kennziffer)}
            >
              <td aria-label={labels.kennziffer + ":"} className="text-end d-flex d-md-table-cell justify-content-between">
                {String(item.kennziffer)}
              </td>
              {labels.isoCode
                ? <td aria-label={labels.isoCode + ":"} className="text-start d-flex d-md-table-cell justify-content-between">
                  {item.isoCode}
                </td>
                : null
              }
              <td aria-label={labels.bezeichnung + ":"} className="text-start d-flex d-md-table-cell justify-content-between w-100">
                {item.bezeichnung}
              </td>
              {keys.map((labelKey, idx) => {
                if (!labels[labelKey]) return null;
                return <td
                  key={labelKey}
                  aria-label={labels[labelKey] + ":"}
                  className="text-end d-flex d-md-table-cell justify-content-between"
                >
                  {!fold[idx]
                    ? <div className="ms-one nowrap">
                      {formater(item[labelKey])}
                    </div>
                    : null
                  }
                </td>;
              })}
            </tr>;
          })}
        </tbody>
      </Table>
    }
    {
      configStorage.isVerseNeo()
        ? <>
          <a
            href={configStorage.getBackToLandingPageLink()}
            className="btn btn-info col-12 col-md-3 col-lg-2"
          >
            Zurück zur Startseite
          </a>
          <BeendenButton className="col-12 col-md-3 col-lg-2" />
        </>
        : null
    }
  </div>;
};
export default Kursliste;
